import ClickAction from "%/dynamic-component-editor/options/click-action";
import {
  CLICK_GESTURE_CLICK,
  CLICK_GESTURE_DOUBLE_CLICK,
} from "%/dynamic-component-editor/constants/click-action-constants";

const GESTURE_EMIT_SOURCE = "gesture";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import { suppressesNavigation } from "%/dynamic-component-editor/helpers/builder-navigation-guard";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import { isBuilderQuiet, isPageExpression, pageLinkValueFor } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-page-target";
import {
  PHASE_CANCEL,
  PHASE_END,
  PHASE_START,
  SINGLE_FINGER,
  TRACKS_SCALE,
  acquireGestureController,
} from "@bloglovin/vue-component-library/gesture";
import {
  BEHAVIOUR_NAVIGATE,
  BEHAVIOUR_SCALE,
  STATE_INLINE,
  applyPresentationState,
  applyScale,
  behaviourOf,
  hapticFor,
  presentationStateOf,
  runBehaviour,
  scaleWithin,
  scrollTowards,
} from "@bloglovin/vue-component-library/behaviour";
import { HAPTIC_LIMIT, playHaptic } from "@bloglovin/vue-component-library/haptics";

export const GestureActionsMixin = {
  mixins: [ContextHrefMixin],
  inject: {
    // The same page-scoped bus the event mixin uses. That mixin lives on the
    // element WRAPPER, so an element cannot borrow its `emitEvent` — it emits
    // on the bus itself.
    dcPageEventBus: {
      from: "dcPageEventBus",
      default: null,
    },
  },
  data() {
    return {
      gestureController: null,
      gestureHost: null,
      gestureScale: 1,
      gestureScaleAtStart: 1,
      gestureTrackedLive: null,
      gestureScaleIsPinned: false,
      gestureRolePlaced: false,
    };
  },
  computed: {
    // An image files its action under Link Action, everything else under
    // Click Action; both store the same shape.
    gestureOption() {
      return this.$options.gestureOption ?? ClickAction;
    },
    gestureActionValue() {
      return this.getOptionValue(this.gestureOption);
    },
    assignedGestures() {
      return ClickAction.assignedGestureEntries(this.gestureActionValue);
    },
    // Inside the builder a gesture is authored, not performed: the canvas keeps
    // its own scrolling, dragging and selection.
    gesturesAreLive() {
      return !suppressesNavigation(this);
    },
    tapBelongsToGestures() {
      const tap = this.assignedGestures.find(entry => entry.id === CLICK_GESTURE_CLICK);
      if (!tap) {
        return false;
      }

      return tap.fingers !== SINGLE_FINGER
        || behaviourOf(this.gestureActionFor(CLICK_GESTURE_CLICK)).kind !== BEHAVIOUR_NAVIGATE
        || this.assignedGestures.some(entry => entry.id === CLICK_GESTURE_DOUBLE_CLICK);
    },
    engineGestures() {
      if (this.tapBelongsToGestures) {
        return this.assignedGestures;
      }

      return this.assignedGestures.filter(entry => entry.id !== CLICK_GESTURE_CLICK);
    },
    // An element that runs its own click action asks this before running it:
    // where the engine owns the tap, it already does the same work.
    gestureOwnsTheClick() {
      return this.gesturesAreLive && this.tapBelongsToGestures;
    },
    pageExpressionActions() {
      const value = this.gestureActionValue;
      return [ClickAction.baseActionFrom(value), ...Object.values(ClickAction.gesturesFrom(value))]
          .filter(action => isPageExpression(action?.page));
    },
  },
  watch: {
    pageExpressionActions: {
      immediate: true,
      handler(actions) {
        actions.forEach(action => pageLinkValueFor(action, "", this.scopedProvidedContexts, { quiet: true }));
      },
    },
    engineGestures: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.syncGestureController();
          this.syncKeyboardPath();
        });
      },
    },
    gesturesAreLive() {
      this.$nextTick(() => {
        this.gestureController?.refresh();
        this.syncKeyboardPath();
      });
    },
  },
  beforeUnmount() {
    this.$el?.removeEventListener?.("keydown", this.runTapFromKeyboard);
    if (typeof document !== "undefined") {
      document.removeEventListener("keydown", this.leaveOnEscape);
    }
    if (this.gestureController) {
      this.gestureController.release();
      this.gestureController = null;
    }
  },
  methods: {
    syncGestureController() {
      const host = this.$el && this.$el.nodeType === Node.ELEMENT_NODE ? this.$el : null;
      if (!host) {
        return;
      }

      if (this.gestureController && (this.gestureHost !== host || !this.engineGestures.length)) {
        this.gestureController.release();
        this.gestureController = null;
      }

      if (!this.engineGestures.length) {
        return;
      }

      if (!this.gestureController) {
        this.gestureHost = host;
        this.gestureController = acquireGestureController(host, {
          gestures: this.engineGestures,
          onGesture: gesture => this.fireGestureAction(gesture),
          onProgress: progress => this.followGesture(progress),
          enabled: () => !suppressesNavigation(this),
        });
        return;
      }

      this.gestureController.update(this.engineGestures);
    },
    // An element that only answers to a pointer cannot be reached by keyboard
    // at all. Where a tap is authored, the element becomes focusable and Enter
    // or Space does what the tap does.
    syncKeyboardPath() {
      const host = this.$el && this.$el.nodeType === Node.ELEMENT_NODE ? this.$el : null;
      if (!host) {
        return;
      }

      const wanted = this.gestureOwnsTheClick;
      if (wanted === this.gestureRolePlaced) {
        return;
      }

      this.gestureRolePlaced = wanted;
      if (!wanted) {
        host.removeEventListener("keydown", this.runTapFromKeyboard);
        host.removeAttribute("tabindex");
        host.removeAttribute("role");
        return;
      }

      host.addEventListener("keydown", this.runTapFromKeyboard);
      if (!host.hasAttribute("tabindex")) {
        host.setAttribute("tabindex", "0");
      }
      if (!host.hasAttribute("role")) {
        host.setAttribute("role", "button");
      }
    },
    runTapFromKeyboard(event) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      this.fireGestureAction(CLICK_GESTURE_CLICK);
    },
    gestureActionFor(gesture) {
      return ClickAction.actionForGesture(this.gestureActionValue, gesture);
    },
    gestureHrefFor(action) {
      if (!action) {
        return null;
      }

      let urlValue = action.value;
      switch (action.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue || null;
        case LinkType.LINK_TYPE_PAGE: {
          urlValue = pageLinkValueFor(action, urlValue, this.scopedProvidedContexts, { quiet: isBuilderQuiet(this) });
          if (urlValue === null) {
            return null;
          }
          const contextHref = this.contextResolvedHref(action, urlValue, this.links.web);
          if (contextHref) {
            return contextHref;
          }
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return null;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${this.links.web}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? null : pageHref;
        }
        default:
          return urlValue || null;
      }
    },
    visitGestureHref(action) {
      const href = this.gestureHrefFor(action);
      if (!href) {
        return false;
      }

      const anchor = document.createElement("a");
      anchor.href = href;
      if (action.openInNewTab) {
        anchor.target = "_blank";
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        return true;
      }
      anchor.click();
      return true;
    },
    fireGestureAction(gesture) {
      if (suppressesNavigation(this)) {
        return false;
      }
      // A pinch the element already followed has arrived at its size; stepping
      // again here would move it a second time for the one gesture.
      if (this.gestureTrackedLive === gesture) {
        this.gestureTrackedLive = null;
        return true;
      }

      return runBehaviour(this.gestureActionFor(gesture), this.gestureBehaviourSurface());
    },
    // A scale follows the fingers while they are still down, and keeps where
    // they left it. Any other behaviour waits for the gesture to settle.
    followGesture(progress) {
      if (progress.tracks !== TRACKS_SCALE || suppressesNavigation(this)) {
        return;
      }

      const behaviour = behaviourOf(this.gestureActionFor(progress.id));
      if (behaviour.kind !== BEHAVIOUR_SCALE) {
        return;
      }

      if (progress.phase === PHASE_START) {
        this.gestureScaleAtStart = this.gestureScale;
        this.gestureScaleIsPinned = false;
      }
      if (progress.phase === PHASE_CANCEL) {
        this.gestureScaleTo(this.gestureScaleAtStart);
        return;
      }

      const wanted = this.gestureScaleAtStart * progress.scale;
      const next = scaleWithin(wanted, behaviour);
      if (next === null) {
        return;
      }

      const moved = Math.abs(next - this.gestureScaleAtStart) > 0.001;
      this.gestureScaleTo(next, { animate: progress.phase === PHASE_END });
      this.reportScaleLimit(next, wanted);
      if (progress.phase === PHASE_END && progress.settled) {
        this.gestureTrackedLive = progress.id;
        // A gesture that was already at the limit changed nothing, and a buzz
        // for nothing reads as a fault.
        if (moved) {
          playHaptic(hapticFor(this.gestureActionFor(progress.id)));
        }
      }
    },
    // What a behaviour is allowed to reach. An element that can play media
    // overrides `gestureMedia` to hand its player over.
    gestureBehaviourSurface() {
      return {
        navigate: action => this.visitGestureHref(action),
        back: () => this.gestureGoBack(),
        emit: eventName => this.gestureEmitEvent(eventName),
        scrollTo: target => this.gestureScrollTo(target),
        currentScale: () => this.gestureScale,
        scale: scale => this.gestureScaleTo(scale),
        present: state => this.gesturePresent(state),
        media: () => this.gestureMedia(),
      };
    },
    gestureMedia() {
      return null;
    },
    // The moment fingers ask for more than the author allowed: one bump, and
    // nothing more until they come back inside the bounds.
    reportScaleLimit(next, wanted) {
      const pinned = Math.abs(wanted - next) > 0.001;
      if (pinned && !this.gestureScaleIsPinned) {
        playHaptic(HAPTIC_LIMIT);
      }

      this.gestureScaleIsPinned = pinned;
    },
    // Moving an element somewhere else takes over its transform, so the scale
    // it was at no longer means anything and starts again from its own size.
    gesturePresent(state) {
      if (!applyPresentationState(this.$el, state)) {
        return false;
      }

      this.gestureScale = 1;
      this.gestureScaleAtStart = 1;
      this.watchForEscape(state);
      return true;
    },
    // An element that has moved must always be leavable, whatever gestures the
    // author did or did not think to add. Escape puts it back.
    watchForEscape(state) {
      if (typeof document === "undefined") {
        return;
      }
      if (state === STATE_INLINE) {
        document.removeEventListener("keydown", this.leaveOnEscape);
        return;
      }

      document.addEventListener("keydown", this.leaveOnEscape);
    },
    leaveOnEscape(event) {
      if (event.key !== "Escape" || presentationStateOf(this.$el) === STATE_INLINE) {
        return;
      }

      this.gesturePresent(STATE_INLINE);
    },
    gestureScaleTo(scale, options = {}) {
      if (!applyScale(this.$el, scale, options)) {
        return false;
      }

      this.gestureScale = scale;
      return true;
    },
    gestureGoBack() {
      if (typeof window === "undefined") {
        return false;
      }
      if (window.history.length > 1) {
        window.history.back();
        return true;
      }

      window.location.href = this.links?.web || "/";
      return true;
    },
    gestureEmitEvent(eventName) {
      const bus = this.dcPageEventBus ?? (typeof window === "undefined" ? null : window.eventBus);
      if (!eventName || !bus) {
        return false;
      }

      bus.emit(eventName, {
        sourceElementId: this.elementId ?? null,
        sourceInstanceKey: this.dcInstanceKey ?? null,
        actionType: GESTURE_EMIT_SOURCE,
      });
      return true;
    },
    gestureScrollTo(target) {
      return scrollTowards(target, this.$el);
    },
  },
};

export default GestureActionsMixin;
